import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAtom } from 'jotai';
import { formProgressAtom } from '../store/form';
import { useSupabaseMutation } from '../hooks/useSupabaseMutation';
import { TechnicalConfig, inverterBrands, solarModuleBrands, mountTypes } from '../types/form';
import { Loader2, Zap, DollarSign as Solar, Wrench, Calendar, ArrowLeft, ArrowRight, Trash2, Lock, Unlock } from 'lucide-react';
import ConfirmationModal from './ConfirmationModal';

const schema = z.object({
  inverter1: z.object({
    brand: z.string().min(1, 'Selecione uma marca'),
    power: z.number().min(0.1).max(100),
    quantity: z.number().min(1).max(99),
    warrantyPeriod: z.number().min(1).max(25)
  }),
  inverter2: z.object({
    brand: z.string(),
    power: z.number().min(0.1).max(100).optional(),
    quantity: z.number().min(1).max(99).optional(),
    warrantyPeriod: z.number().min(1).max(25).optional()
  }).optional(),
  solarModules: z.object({
    brand: z.string().min(1, 'Selecione uma marca'),
    power: z.number().min(100).max(800),
    quantity: z.number().min(1).max(999)
  }),
  installationType: z.enum(['Ground', 'Roof', 'Other']),
  otherTypeDescription: z.string().optional(),
  installationDays: z.number().min(1, 'Mínimo de 1 dia').max(365, 'Máximo de 365 dias')
});

export default function TechnicalConfigForm() {
  const [formProgress, setFormProgress] = useAtom(formProgressAtom);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showUnlockModal, setShowUnlockModal] = React.useState(false);
  const { mutateAsync: saveTechnicalConfig } = useSupabaseMutation('technical_configs');
  const [showInverter2, setShowInverter2] = React.useState(false);

  const isLocked = formProgress.isLocked;

  const handleUnlockContract = () => {
    setFormProgress(prev => ({
      ...prev,
      isLocked: false
    }));
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors }
  } = useForm<TechnicalConfig>({
    resolver: zodResolver(schema),
    defaultValues: formProgress.data.technicalConfig
  });

  const watchedValues = watch();
  const valuesRef = React.useRef(watchedValues);
  const installationType = watch('installationType');

  React.useEffect(() => {
    valuesRef.current = watchedValues;
  }, [watchedValues]);

  React.useEffect(() => {
    if (formProgress.data.technicalConfig) {
      const config = formProgress.data.technicalConfig;
      if (config.inverter1) {
        setValue('inverter1.brand', config.inverter1.brand);
        setValue('inverter1.power', config.inverter1.power);
        setValue('inverter1.quantity', config.inverter1.quantity);
        setValue('inverter1.warrantyPeriod', config.inverter1.warrantyPeriod);
      }
      if (config.solarModules) {
        setValue('solarModules.brand', config.solarModules.brand);
        setValue('solarModules.power', config.solarModules.power);
        setValue('solarModules.quantity', config.solarModules.quantity);
      }
      if (config.installationType) {
        setValue('installationType', config.installationType);
      }
      if (config.installationDays) {
        setValue('installationDays', config.installationDays);
      }
      if (config.inverter2?.brand) {
        setShowInverter2(true);
        setValue('inverter2.brand', config.inverter2.brand);
        setValue('inverter2.power', config.inverter2.power);
        setValue('inverter2.quantity', config.inverter2.quantity);
        setValue('inverter2.warrantyPeriod', config.inverter2.warrantyPeriod);
      }
    }
  }, [formProgress.data.technicalConfig, setValue]);

  React.useEffect(() => {
    return () => {
      const currentValues = valuesRef.current;
      const hasData = currentValues.inverter1?.brand || currentValues.solarModules?.brand;
      if (hasData) {
        setFormProgress(prev => ({
          ...prev,
          data: {
            ...prev.data,
            technicalConfig: {
              ...prev.data.technicalConfig,
              ...currentValues
            }
          }
        }));
      }
    };
  }, [setFormProgress]);

  const onSubmit = async (data: TechnicalConfig) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Validate required IDs
      if (!formProgress.data.customerId) {
        throw new Error('Por favor, preencha as informações do cliente primeiro.');
      }

      // Prepare inverter2 data based on showInverter2 state
      const inverter2Data = showInverter2 ? {
        inverter2_brand: data.inverter2?.brand || null,
        inverter2_power: data.inverter2?.power || null,
        inverter2_quantity: data.inverter2?.quantity || null,
        inverter2_warranty_period: data.inverter2?.warrantyPeriod || null
      } : {
        inverter2_brand: null,
        inverter2_power: null,
        inverter2_quantity: null,
        inverter2_warranty_period: null
      };

      // Prepare the complete payload
      const payload = {
        id: formProgress.data.technicalConfigId,
        customer_id: formProgress.data.customerId || null,
        inverter1_brand: data.inverter1.brand,
        inverter1_power: data.inverter1.power,
        inverter1_quantity: data.inverter1.quantity,
        inverter1_warranty_period: data.inverter1.warrantyPeriod,
        ...inverter2Data,
        solar_modules_brand: data.solarModules.brand,
        solar_modules_power: data.solarModules.power,
        solar_modules_quantity: data.solarModules.quantity,
        installation_type: data.installationType,
        installation_days: data.installationDays
      };

      const savedConfig = await saveTechnicalConfig(payload);

      if (!savedConfig || !savedConfig.id) {
        throw new Error('Failed to save technical configuration: No valid response received');
      }
      
      setFormProgress(prev => ({
        ...prev,
        currentStep: 'financial-terms',
        completedSteps: prev.completedSteps.includes('technical-config')
          ? prev.completedSteps
          : [...prev.completedSteps, 'technical-config'],
        data: {
          ...prev.data,
          // Preserve all existing data when updating
          technicalConfig: data,
          technicalConfigId: savedConfig.id
        }
      }));
    } catch (error) {
      console.error('Error saving technical config:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to save technical configuration. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Configuração Técnica do Sistema</h2>
        {isLocked && (
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
              <Lock className="w-4 h-4" />
              Contrato Bloqueado
            </span>
            <button
              type="button"
              onClick={() => setShowUnlockModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Unlock className="w-4 h-4" />
              Desbloquear para Editar
            </button>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        onConfirm={handleUnlockContract}
        title="Desbloquear Contrato"
        message="Tem certeza que deseja desbloquear o contrato para edicao? Isso permitira alterar todos os campos do formulario."
        confirmText="Sim, Desbloquear"
        cancelText="Cancelar"
      />

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <fieldset disabled={isLocked} className={isLocked ? 'opacity-60' : ''}>
        {/* Inversor Principal */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2" />
            Inversor Principal
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Fabricante</label>
              <select
                {...register('inverter1.brand')}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione o fabricante</option>
                {inverterBrands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
              {errors.inverter1?.brand && <p className="text-red-500 text-sm">{errors.inverter1.brand.message}</p>}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Potência (kW)</label>
              <input
                type="number"
                step="0.1"
                {...register('inverter1.power', { valueAsNumber: true })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.inverter1?.power && <p className="text-red-500 text-sm">{errors.inverter1.power.message}</p>}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Quantidade</label>
              <input
                type="number"
                {...register('inverter1.quantity', { valueAsNumber: true })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.inverter1?.quantity && <p className="text-red-500 text-sm">{errors.inverter1.quantity.message}</p>}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Garantia (anos)</label>
              <input
                type="number"
                {...register('inverter1.warrantyPeriod', { valueAsNumber: true })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.inverter1?.warrantyPeriod && <p className="text-red-500 text-sm">{errors.inverter1.warrantyPeriod.message}</p>}
            </div>
          </div>
        </div>

        {/* Toggle Inversor Secundário */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="showInverter2"
            checked={showInverter2}
            onChange={(e) => setShowInverter2(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="showInverter2" className="text-sm font-medium text-gray-700">
            Adicionar inversor secundário
          </label>
        </div>

        {/* Inversor Secundário */}
        {showInverter2 && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
              <Zap className="w-5 h-5 mr-2" />
              Inversor Secundário
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Fabricante</label>
                <select
                  {...register('inverter2.brand')}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione o fabricante</option>
                  {inverterBrands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Potência (kW)</label>
                <input
                  type="number"
                  step="0.1"
                  {...register('inverter2.power', { valueAsNumber: true })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Quantidade</label>
                <input
                  type="number"
                  {...register('inverter2.quantity', { valueAsNumber: true })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Garantia (anos)</label>
                <input
                  type="number"
                  {...register('inverter2.warrantyPeriod', { valueAsNumber: true })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}

        {/* Módulos Solares */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <Solar className="w-5 h-5 mr-2" />
            Módulos Solares
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Fabricante</label>
              <select
                {...register('solarModules.brand')}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione o fabricante</option>
                {solarModuleBrands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
              {errors.solarModules?.brand && <p className="text-red-500 text-sm">{errors.solarModules.brand.message}</p>}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Potência (W)</label>
              <input
                type="number"
                {...register('solarModules.power', { valueAsNumber: true })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.solarModules?.power && <p className="text-red-500 text-sm">{errors.solarModules.power.message}</p>}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Quantidade</label>
              <input
                type="number"
                {...register('solarModules.quantity', { valueAsNumber: true })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.solarModules?.quantity && <p className="text-red-500 text-sm">{errors.solarModules.quantity.message}</p>}
            </div>
          </div>
        </div>

        {/* Tipo de Instalação */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <Wrench className="w-5 h-5 mr-2" />
            Tipo de Instalação
          </h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Tipo</label>
              <select
                {...register('installationType')}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione o tipo</option>
                {mountTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {errors.installationType && <p className="text-red-500 text-sm">{errors.installationType.message}</p>}
            </div>

            {installationType === 'Other' && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Descrição do tipo de instalação</label>
                <input
                  type="text"
                  {...register('otherTypeDescription')}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Descreva o tipo de instalação"
                />
                {errors.otherTypeDescription && <p className="text-red-500 text-sm">{errors.otherTypeDescription.message}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Dias para Instalação */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            Prazo de Instalação
          </h3>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Dias para Instalação</label>
            <input
              type="number"
              {...register('installationDays', { valueAsNumber: true })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Número de dias"
            />
            {errors.installationDays && <p className="text-red-500 text-sm">{errors.installationDays.message}</p>}
          </div>
        </div>
        </fieldset>

        {/* Botões */}
        <div className="flex justify-between pt-4 gap-2 flex-wrap sm:flex-nowrap">
          <button
            type="button"
            onClick={() =>
              setFormProgress(prev => ({
                ...prev,
                currentStep: 'installation-location'
              }))
            }
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </button>

          <button
            type="button"
            onClick={() => {
              reset({
                inverter1: {
                  brand: '',
                  power: 0,
                  quantity: 0,
                  warrantyPeriod: 0
                },
                solarModules: {
                  brand: '',
                  power: 0,
                  quantity: 0
                },
                installationType: 'Roof',
                installationDays: 0,
                otherTypeDescription: ''
              });
              setShowInverter2(false);
            }}
            disabled={isLocked}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Limpar Campos
          </button>

          <button
            type="submit"
            disabled={isSubmitting || isLocked}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                Próximo
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}